'use client';

import { Box, Button } from '@mui/material';
import { genId } from '@ocodelib/ex-util';
import { StackRow } from '@ocodelib/mui';
import { cssVars } from '@ocodelib/mui/helper';
import { useBehaviorSubject } from '@ocodelib/mui/hooks';
import { downloadBlob } from '@ocodelib/util/web';
import { useP5BasicApp } from '@repo/p5-basic-app';
import type { ProjectFile } from '@repo/project-interface';
import { DefaultProjectSerializer } from '@repo/project-serializer/impl';
import clsx from 'clsx';
import JSZip from 'jszip';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { BootstrapInput } from '../../../components/BootstrapInput';
import { LoginButton } from '../../../components/LoginButton';
import { LogoLinkButton } from '../../../components/LogoLinkButton';
import {
  ProjectPickerDialog,
  type ProjectPickerDialogProps,
} from '../../../dialogs/ProjectPickerDialog';
import { ProjectSaveDialog, type ProjectSaveDialogProps } from '../../../dialogs/ProjectSaveDialog';
import { LoginDialogOpenEvent } from '../../../events/login-dialog-open';
import { useLoginUser } from '../../../store/useAuthStore';
import { ProjectOpenButton } from './top-bar/ProjectOpenButton';
import { ProjectSaveButton } from './top-bar/ProjectSaveButton';

type DialogParams =
  | {
      id: 'ProjectSaveDialog';
      props: ProjectSaveDialogProps;
    }
  | {
      id: 'ProjectPickerDialog';
      props: ProjectPickerDialogProps;
    };

interface Props {
  title?: string;
  className?: string;
  backLink?: string;
}

export function WorkspaceTopbar(props: Props) {
  const { className, title } = props;
  const rootRef = useRef<HTMLElement | null>(null);
  const { app } = useP5BasicApp();
  const { profile } = useLoginUser();
  const [dialogParams, setDialogParams] = useState<DialogParams>();
  const projectName = useBehaviorSubject(app.projectName$);
  const projectKey = useBehaviorSubject(app.projectKey$);

  // 저장 다이얼로그 띄우기
  const openSaveDialog = (
    projectId: string | undefined,
    projectName: string,
    files: File[],
    thumbUrl: string,
  ) => {
    const createDataFile = () => DefaultProjectSerializer.createDataFile(projectName, files);

    setDialogParams({
      id: 'ProjectSaveDialog',
      props: {
        breed: 'p5',
        open: true,
        onClose: () => setDialogParams(undefined),
        projectId,
        projectName,
        thumbUrl,
        createDataFile,
        onSaved: (project) => {
          console.log('saved-project', project);
          setDialogParams(undefined);
          app.setProjectName(project.projectName);
          app.setProjectKey({
            projectId: project.projectId,
            accountId: project.accountId,
          });
        },
      },
    });
  };

  // 새작품으로 저장 버튼 클릭
  const handleClickSaveNew = () => {
    if (!profile) {
      toast.warn('저장을 위해 로그인이 필요합니다.');
      setTimeout(() => {
        LoginDialogOpenEvent.emit(Date.now());
      }, 700);
      return;
    }

    const snapshotUrl = app.snapshotImageUrl$.value;
    if (!snapshotUrl) {
      toast.warn(
        '저장을 위해 썸네일 이미지를 생성해야 합니다. 실행중인 상태에서 스냅샷 버튼을 클릭해주세요.',
      );
      return;
    }

    const files = app.files$.value;
    if (files.length === 0) {
      // 이런 경우는 없음
      toast.warn('저장할 파일이 없습니다');
      return;
    }

    const fileObjects = files.map((it) => {
      const blob = new Blob([it.content]);
      return new File([blob], it.name, { type: 'text/plain', lastModified: Date.now() });
    });

    openSaveDialog(undefined, projectName, fileObjects, snapshotUrl);
  };

  const handleClickSaveUpdate = () => {
    if (!profile) {
      // 업데이트이므로 이런 경우가 발생하면 안됨
      toast.warn('작품 저장을 위해 로그인이 필요합니다.');
      setTimeout(() => {
        LoginDialogOpenEvent.emit(Date.now());
      }, 700);
      return;
    }

    if (!projectKey) {
      // 업데이트이므로 이런 경우가 발생하면 안됨
      toast.warn('프로젝트 키가 존재하지 않습니다.');
      return;
    }

    const snapshotUrl = app.snapshotImageUrl$.value;
    if (!snapshotUrl) {
      toast.warn(
        '저장을 위해 썸네일 이미지를 생성해야 합니다. 실행중인 상태에서 스냅샷 버튼을 클릭해주세요.',
      );
      return;
    }

    const files = app.files$.value;
    if (files.length === 0) {
      // 이런 경우는 없음
      toast.warn('저장할 파일이 없습니다');
      return;
    }
    const fileObjects = files.map((it) => {
      const blob = new Blob([it.content]);
      return new File([blob], it.name, { type: 'text/plain', lastModified: Date.now() });
    });

    openSaveDialog(projectKey.projectId, projectName, fileObjects, snapshotUrl);
  };

  // 파일 불러오기 버튼 클릭
  const handleClickOpenFromFileSelect = () => {
    //
  };

  const loadProjectFile = async (profileFileUrl: string): Promise<ProjectFile[]> => {
    const zipFileBlob = await fetch(profileFileUrl).then((response) => response.blob());
    const jszip = new JSZip();
    const zipData = await jszip.loadAsync(zipFileBlob);

    const resultFiles = [] as ProjectFile[];
    const files = Object.values(zipData.files);
    for (const file of files) {
      const { dir, name } = file;
      if (dir) {
        continue;
      }
      // console.log('XXX relativePath=', relativePath, file);
      const result = await file.async('blob');
      const content = await result.text();
      let language = '';
      const fileNameLower = file.name.toLowerCase();
      if (fileNameLower.endsWith('.js')) {
        language = 'javascript';
      } else if (fileNameLower.endsWith('.html') || fileNameLower.endsWith('.htm')) {
        language = 'html';
      } else if (fileNameLower.endsWith('.css')) {
        language = 'css';
      } else if (fileNameLower.endsWith('.py')) {
        language = 'python';
      }

      resultFiles.push({
        fileKey: genId('file_', (id) => resultFiles.some((it) => it.fileKey === id)),
        name,
        path: name,
        content,
        language,
      });
    }
    return resultFiles;
  };

  // 내작품에서 선택
  const handleClickOpenFromServer = () => {
    if (!profile) {
      toast.warn('로그인이 필요합니다.');
      setTimeout(() => {
        LoginDialogOpenEvent.emit(Date.now());
      }, 700);
      return;
    }

    // 프로젝트 목록 다이얼로그
    setDialogParams({
      id: 'ProjectPickerDialog',
      props: {
        open: true,
        breed: 'p5',
        disableBreedSelect: true,
        onClose: () => setDialogParams(undefined),
        onSelected: (pjt) => {
          setDialogParams(undefined);
          const { projectFileUrl } = pjt;
          console.log('pjt selected', pjt);
          loadProjectFile(projectFileUrl).then((projectFiles) => {
            app.resetProject({
              breed: 'p5',
              project: {
                accountId: pjt.accountId,
                projectId: pjt.projectId,
              },
              projectName: pjt.projectName,
              data: projectFiles,
            });
          });
        },
      },
    });
  };

  // 저장버튼의 서브메뉴에 저장(업데이트) 버튼을 표시할지 여부
  // 현재 작품의 소유자가 본인인 경우에만 업데이트 버튼을 표시
  const visibleUpdateBtn = Boolean(profile && profile.accountId == projectKey?.accountId);

  return (
    <Box
      ref={rootRef}
      className={clsx('WorkspaceTopbar-root', className)}
      sx={{
        position: 'relative',
        height: 52,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: 'primary.contrastText',
        bgcolor: 'primary.dark',
        boxShadow: 'none',
        '& .MuiButton-root': {
          color: 'primary.contrastText',
          // '&:hover': {
          //   color: (theme) => alpha(theme.palette.primary.contrastText, 0.85),
          // },
        },
      }}
    >
      <StackRow
        alignItems="center"
        spacing={1}
        // sx={{
        //   '& .MuiButton-root': {
        //     color: 'primary.contrastText',
        //     '&:hover': {
        //       color: (theme) => alpha(theme.palette.primary.contrastText, 0.85),
        //     },
        //   },
        // }}
      >
        <LogoLinkButton href={props.backLink || '/coding/list'} title="P5.js" />
        <BootstrapInput
          value={projectName}
          size="small"
          autoComplete="off"
          onChange={(e) => {
            app.setProjectName(e.target.value);
          }}
        />

        <ProjectSaveButton
          visibleUpdateBtn={visibleUpdateBtn}
          onClickDownloadBtn={() => {
            app.serializeToPjtFile().then((zipFile) => {
              if (zipFile) {
                downloadBlob(zipFile, zipFile.name);
              }
            });
          }}
          onClickSaveNewBtn={() => {
            handleClickSaveNew();
          }}
          onClickSaveUpdateBtn={() => {
            handleClickSaveUpdate();
          }}
        />
      </StackRow>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Button
          color="inherit"
          component="div"
          onClick={() => {
            app.resetToNewProject();
          }}
        >
          새로 만들기
        </Button>
        <ProjectOpenButton
          onClickOpenFromFileSelect={handleClickOpenFromFileSelect}
          onClickOpenFromServerBtn={handleClickOpenFromServer}
        />
        <Box
          sx={{
            position: 'relative',
            '&::before': {
              position: 'absolute',
              content: '""',
              top: '50%',
              left: 0,
              transform: 'translateY(-50%)',
              height: 16,
              width: '1px',
              bgcolor: cssVars.rgba('primary', 'contrastText', 0.3),
            },
          }}
        >
          <LoginButton whiteText />
        </Box>
      </Box>
      {dialogParams?.id === 'ProjectSaveDialog' && <ProjectSaveDialog {...dialogParams.props} />}
      {dialogParams?.id === 'ProjectPickerDialog' && (
        <ProjectPickerDialog {...dialogParams.props} />
      )}
    </Box>
  );
}
